// src/features/statistics/components/GeneralStatisticsTab.tsx

import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAccountsGeneralStats } from '../hooks/useAccountsGeneralStats';
import { useTikTokGeneralStats } from '../hooks/useTikTokGeneralStats';
import { useDemographicsFilter } from '../hooks/useDemographicsFilter';
import { dateRangeToSearchString, useDateRangeParams } from '../hooks/useDateRangeParams';
import { resolveDateRange } from '../lib/date-range';
import { sortRows } from '../lib/sort-rows';
import { parseGeneralView, writeGeneralView, type GeneralStatsView } from '../lib/general-stats-params';
import { EMPTY_FILTER, isFilterEmpty } from '../types/demographics-filter';
import { AccountsStatsTable } from './AccountsStatsTable';
import { GeneralStatsToolbar } from './GeneralStatsToolbar';

import type { DemographicsFilter } from '../types/demographics-filter';
import type { SortDirection, SortKey } from '../types/account-stats';

export function GeneralStatisticsTab(): JSX.Element {
  const tableRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const [dateRangeOption, setDateRangeOption] = useDateRangeParams();
  // Теги, демография, сортировка и «Динамика» — в адресе: возврат со страницы
  // аккаунта и ссылка коллеге открывают ту же таблицу.
  const [searchParams, setSearchParams] = useSearchParams();
  const view = useMemo(() => parseGeneralView(searchParams), [searchParams]);
  const { comparePrev, sortKey, sortDir, tags: selectedTags, filter: activeFilter } = view;
  const updateView = useCallback(
    (patch: Partial<GeneralStatsView>) =>
      setSearchParams(
        (prev) => writeGeneralView(new URLSearchParams(prev), { ...parseGeneralView(prev), ...patch }),
        { replace: true },
      ),
    [setSearchParams],
  );
  const setSelectedTags = (tags: string[]) => updateView({ tags });
  const setActiveFilter = (filter: DemographicsFilter) => updateView({ filter });

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [pendingFilter, setPendingFilter] = useState<DemographicsFilter>(activeFilter);

  const resolvedRange = useMemo(() => resolveDateRange(dateRangeOption), [dateRangeOption]);
  const { rows, isLoading, isFetching, refetch } = useAccountsGeneralStats(
    resolvedRange,
    comparePrev
  );
  const { rows: tiktokRows } = useTikTokGeneralStats(resolvedRange);

  const allTags = useMemo(() => Array.from(new Set(rows.flatMap(r => r.tags))).sort(), [rows]);

  const tagFilteredRows = useMemo(
    () =>
      selectedTags.length === 0
        ? rows
        : rows.filter(r => selectedTags.some((t: string) => r.tags.includes(t))),
    [rows, selectedTags]
  );

  const {
    filteredRows: demographicsFilteredRows,
    unavailableIds,
    isFetchingDemographics,
    availableCountries,
    availableCities,
  } = useDemographicsFilter(tagFilteredRows, activeFilter, dateRangeOption, !isLoading && tagFilteredRows.length > 0);

  // TikTok rows carry no tags/demographics, so drop them when either filter is
  // active (a tag or country filter can't match a TikTok account).
  const tiktokVisible = useMemo(
    () => (selectedTags.length === 0 && isFilterEmpty(activeFilter) ? tiktokRows : []),
    [tiktokRows, selectedTags, activeFilter]
  );

  const sortedRows = useMemo(
    () => sortRows([...demographicsFilteredRows, ...tiktokVisible], sortKey, sortDir),
    [demographicsFilteredRows, tiktokVisible, sortKey, sortDir]
  );

  function handleSort(key: SortKey): void {
    if (key === sortKey) {
      updateView({ sortDir: (sortDir === 'asc' ? 'desc' : 'asc') as SortDirection });
    } else {
      updateView({ sortKey: key, sortDir: 'desc' });
    }
  }

  function handleRowClick(accountId: string): void {
    // Вид таблицы уходит в адрес аккаунта, чтобы «назад» вернул его целиком.
    const back = writeGeneralView(new URLSearchParams(dateRangeToSearchString(dateRangeOption)), view);
    navigate(`/analytics/account/${encodeURIComponent(accountId)}?${back.toString()}`);
  }

  function handleFilterApply(): void {
    setActiveFilter({ ...pendingFilter });
  }

  function handleFilterReset(): void {
    setPendingFilter(EMPTY_FILTER);
    setActiveFilter(EMPTY_FILTER);
  }

  return (
    <div ref={tableRef}>
      <AccountsStatsTable
        rows={sortedRows}
        sortKey={sortKey}
        sortDirection={sortDir}
        onSort={handleSort}
        isLoadingAccounts={isLoading}
        showDeltas={comparePrev}
        onRowClick={handleRowClick}
        activeFilter={isFilterEmpty(activeFilter) ? null : activeFilter}
        onFilterReset={handleFilterReset}
        unavailableDemographicsIds={unavailableIds}
        toolbar={
          <GeneralStatsToolbar
            sortedRows={sortedRows}
            tableRef={tableRef}
            dateRangeOption={dateRangeOption}
            onDateRangeChange={setDateRangeOption}
            comparePrev={comparePrev}
            onComparePrevToggle={() => updateView({ comparePrev: !comparePrev })}
            isFetching={isFetching}
            onRefresh={() => refetch({ forceRefresh: true })}
            isLoadingTags={isLoading}
            allTags={allTags}
            selectedTags={selectedTags}
            onTagsChange={setSelectedTags}
            isFilterPanelOpen={isFilterPanelOpen}
            onFilterPanelOpenChange={setIsFilterPanelOpen}
            pendingFilter={pendingFilter}
            onPendingFilterChange={setPendingFilter}
            activeFilter={activeFilter}
            onFilterApply={handleFilterApply}
            onFilterReset={handleFilterReset}
            availableCountries={availableCountries}
            availableCities={availableCities}
            isFetchingDemographics={isFetchingDemographics}
          />
        }
      />
    </div>
  );
}
